// Battlemap layout (v0.8.9, Phase 0 prototype).
//
// Pure, deterministic placement for the sprint-as-battlefield view.
// No three.js here on purpose: this is the part that survives if we
// change renderer (3D → 2D, canvas → SVG), and it's the part worth
// testing. The prototype page turns these coordinates into meshes.
//
// DESIGN RULES
//
//  1. Stable. Every position is derived from the ticket key via a
//     hash, so a ticket sits in the same place on every reload. The
//     map only changes when the *data* changes — you cannot notice
//     movement if the board is moving on its own.
//
//  2. Committed work is the battlefield; uncommitted work is off it.
//     The whole point of the view is the predictability question
//     ("how much of the original promise is finished"), so side
//     quests are placed OUTSIDE the field with their own tally. A
//     busy-looking battlefield must never read as progress.
//
//  3. One area. A single continuous battlefield, ordered by depth
//     rather than by time: the enemy is far, your work is near. There
//     is deliberately NO time axis on the ground.
//
//     An earlier version split the plane into "elapsed" and
//     "remaining" with a sweeping boundary line. It was removed: a
//     mob's position comes from a hash, so tagging a piece as "the
//     clock has passed it" was a statement about the hash, not about
//     schedule. Per-ticket time position is unknowable — we have no
//     due dates — so the ground doesn't pretend to know one.
//
// Coordinate space (world units, 1 unit ≈ 1 metre at default zoom):
//
//      -z  ┌──────────────────────────────┐  far — the enemy
//          │        BOSS KEEP             │  z ≈ -68
//          ├──────────────────────────────┤
//          │      [Camp]  [Camp]          │  goals at z ≈ -42
//          ├──────────────────────────────┤
//          │   committed mobs, no goal    │  z ∈ [-25, 8]
//          ├──────────────────────────────┤
//          │   the graveyard: side quests │  z ∈ [23, 31]
//          │   promise)                   │
//      +z  └──────────────────────────────┘  near — your own lines
//
//   The zones are ordered so the depth reads as distance to the
//   enemy. They're spaced so no two zones can overlap: goal mobs sit
//   on rings of radius 5.5-10 around a camp, which spans z ∈ [-52,
//   -32], and the open field starts at -25.
//
//   x is centred on 0; the field width is a constant, not derived
//   from the ticket count (see placeKeys).

import { stableHash } from '../boss/sprites';
import { MAX_APPENDAGES } from './beast';

export interface LayoutInput {
  /** Committed ticket keys — the promise. The denominator. */
  committed: string[];
  /** Committed keys that have reached a terminal status. */
  cleared: string[];
  /** Goals, each with the committed ticket keys it owns. */
  goals: Array<{ id: string; name: string; keys: string[] }>;
  /** Epics / bosses. Not part of the commitment — they span sprints. */
  bosses: Array<{ id: string; name: string; hp: number; damage: number }>;
  /** Tickets closed in the window that were NOT in the commitment. */
  sideQuests: string[];
  /** Still-open work that arrived mid-sprint and was never committed. */
  reinforcements?: string[];
  /** Current status per key, for the progress indicator. Absent means
   *  no status seen, which renders as not engaged. */
  statusOf?: Record<string, string>;
  /** ticket key -> owning user id (most-recent-actor). Optional. */
  ownerOf: Record<string, string>;
  /** Everyone who acted on each key, accountable or not. Used to give a
   *  position to people whose only relationship to the work is
   *  assistance — without it their engagement lines cannot be drawn. */
  actorsOf?: Record<string, string[]>;
  /** Who each ticket is ASSIGNED to, where the payload knew. Optional:
   *  the assignee is only recorded forward-only, so most history has
   *  none and the accountable person falls back to ownerOf. */
  assigneeOf?: Record<string, string>;
  /** ticket key -> boss id, for tickets that are an epic's minions.
   *  A ticket can be committed AND a minion — killing it then does
   *  double duty. Optional. */
  minionOf?: Record<string, string>;
  /** ticket key -> ISO timestamp of its last status movement. Absent
   *  means it hasn't moved at all this sprint. Optional. */
  lastTouchOf?: Record<string, string>;
  /** Sprint window. Feeds the elapsed number shown in the header —
   *  it is not used to place anything. */
  startsAt: string;
  endsAt: string;
}

/** The stages a piece is ATTACKED through, in order.
 *
 *  Death is one step past the last of these, so nothing reaches 1 while
 *  it is alive — the final blow is the transition to Done, which turns
 *  the piece into a headstone. A piece in Acceptance Testing has three
 *  of four blows landed, which is the honest way to say "nearly dead". */
export const ENGAGED_STAGES = ['In Progress', 'Review', 'Testing', 'Acceptance Testing'];

/** Statuses the map treats as actively being fought. A piece in one of
 *  these is engaged. See appendagesFor for how status becomes SHAPE. */
export function isEngaged(status: string | null | undefined): boolean {
  return status ? ENGAGED_STAGES.includes(status) : false;
}

/** The status the map draws a shield for. Mirrors the api's
 *  BLOCKED_STATUSES; the status string is the contract between them
 *  either way, and the stages above are hardcoded here for the same
 *  reason. */
export const BLOCKED_STATUS = 'Blocked';

/**
 * How many horns the beast still has.
 *
 * The operator's rule, and it reads as a sentence: a ticket enters the field
 * whole and loses a limb at every stage it survives.
 *
 *   To Do              5   untouched
 *   In Progress        4
 *   Review             3
 *   Testing            2
 *   Acceptance Testing 1   "it's only a flesh wound"
 *   terminal           0   dead, and no longer drawn at all
 *
 * Anything the map does not recognise as an engaged stage has not STARTED
 * fighting - Blocked, and any status the vocabulary does not know - so it
 * keeps all five. A stopped beast is not a wounded one.
 */
export function appendagesFor(status: string | null | undefined): number {
  if (!status) return MAX_APPENDAGES;
  const i = ENGAGED_STAGES.indexOf(status);
  return i < 0 ? MAX_APPENDAGES : MAX_APPENDAGES - (i + 1);
}

/** How far along the road to done, 0..1. 0 for anything not on it:
 *  To Do has not been engaged, and Blocked is not progressing. */
export function deathProgress(status: string | null | undefined): number {
  if (!status) return 0;
  const i = ENGAGED_STAGES.indexOf(status);
  return i < 0 ? 0 : i / ENGAGED_STAGES.length;
}

export interface MobPiece {
  key: string;
  x: number;
  z: number;
  cleared: boolean;
  goalId: string | null;
  ownerId: string | null;
  /** True for unplanned work that arrived mid-sprint. Position already
   *  says so (they stand in their own band); this is for anything that
   *  needs to label them. */
  isReinforcement: boolean;
  /** Boss id when this ticket belongs to an epic's HP pool. */
  minionBossId: string | null;
  /** Vertical lift so pieces sharing a grid cell stack instead of
   *  blending into one another. 0 for the occupant of an uncontested
   *  cell. */
  y: number;
  /** Elevation of the surface the piece stands on. 0 on the field,
   *  PLATFORM_TOP inside an encampment, which is level with the field. */
  groundY: number;
  /** ISO timestamp of the last status movement, or null if the ticket
   *  hasn't moved this sprint. The caller decides what counts as
   *  stale — that's policy, not geometry. */
  lastTouchAt: string | null;
  /** Actively being fought — In Progress through Acceptance Testing.
   *  Drawn with a sword. False for To Do, which has not been attacked,
   *  and that is the ONLY thing distinguishing those two states. */
  engaged: boolean;
  /** Horns remaining, 0..MAX_APPENDAGES. The status as a SHAPE rather than
   *  as a marker bolted on in front of the piece. */
  appendages: number;
  /** Blocked: work that has STOPPED. Not a kind of work, and not a point on
   *  the road to done — which is why it is neither a colour nor a depth.
   *  It gets a DOME over the piece, and it keeps every horn: a beast
   *  standing still is not a beast dying. */
  blocked: boolean;
  /** How far along the road to done, 0..1. The renderer SINKS the piece
   *  into the ground by this fraction, so progress is read in the
   *  silhouette — measured, one piece is 8 screen px wide, so anything
   *  drawn beside it would overlap its neighbours. */
  progress: number;
}

export interface GoalPiece {
  id: string;
  name: string;
  x: number;
  z: number;
  total: number;
  cleared: number;
  /** Goal keys that aren't in the commitment — a config mistake the
   *  operator should be able to see. Rendered faded. */
  orphanKeys: string[];
}

export interface BossPiece {
  id: string;
  name: string;
  x: number;
  z: number;
  hpRemaining: number;
  hpMax: number;
}

export interface HeroPiece {
  id: string;
  x: number;
  z: number;
  liveMobs: number;
}

export interface GravePiece {
  key: string;
  x: number;
  z: number;
}

export interface Battlefield {
  mobs: MobPiece[];
  /** Unplanned work still in flight. Same shape as a mob so it can be
   *  clicked and tethered like one. */
  reinforcements: MobPiece[];
  goals: GoalPiece[];
  bosses: BossPiece[];
  heroes: HeroPiece[];
  graveyard: GravePiece[];
  /** Committed mobs plus reinforcements — everything standing on the
   *  field. Counts on the footer read this rather than mobs alone. */
  standingTotal: number;
  /** 0 = sprint just started, 1 = sprint over. Reported as a number
   *  only — it is no longer drawn on the ground. */
  elapsedFraction: number;
  /** Committed mobs still standing. */
  remaining: number;
  committedTotal: number;
  /** Still-standing committed mobs that are also a boss's minion. */
  minionsStanding: number;
}

// One plane. These are the z-bands within it, far (negative) to near
// (positive). Spacing matters: each band is clear of the next.
export const PLANE_Z_FAR = -80;
export const PLANE_Z_NEAR = 32;
export const BOSS_Z = -68;
export const GOAL_Z = -42;
export const FIELD_Z_FAR = -25;
export const FIELD_Z_NEAR = 8;
/** Reinforcements: unplanned work that arrived mid-sprint, still open.
 *  Its own band between the promise and the graveyard — nearer to the
 *  viewer than the commitment, so it cannot be mistaken for part of it,
 *  but not banished as far as work that is over. */
export const REINFORCE_Z_FAR = 10;
export const REINFORCE_Z_NEAR = 20;
/** The graveyard. Closed work that was never promised - side quests.
 *  Rows of headstones rather than a scatter, because scattered markers
 *  read as debris and rows read as a graveyard. */
export const GRAVE_Z_FAR = 23;
export const GRAVE_Z_NEAR = 31;
export const GRAVE_ROWS = 3;
export const GRAVE_COLS = 30;

/** Top surface of a goal encampment platform. Mobs belonging to a
 *  goal stand on this, not on the ground — otherwise they render
 *  half-buried in the platform. Shared here rather than in the page
 *  because it changes where the pieces are, not just how they look. */
/** The ground inside an encampment is the SAME ground as the field.
 *
 *  It used to be a raised platform, which hid the bottom of every piece
 *  standing on it — and once a piece sinks as it nears done, that hid
 *  exactly the thing the sinking is there to show. Encampments are
 *  marked by a fence around their edge now, leaving the interior flat.
 *
 *  Kept as a named constant because it is a surface height, not a magic
 *  zero, and the day a camp does sit higher this is where it goes. */
export const PLATFORM_TOP = 0;

const TWO_PI = Math.PI * 2;

/**
 * Final avalanche mix for a 32-bit hash (the `lowbias32` style mix).
 *
 * This is not decoration — it fixes a real bug. `stableHash` is
 * FNV-1a, whose last step is `h = (h ^ c) * prime`. Two salts that
 * differ only in a trailing character therefore produce hashes a
 * near-constant offset apart: `h(key|fgx)` and `h(key|fgz)` came out
 * 0.8% apart on real ticket keys. Since gx and gz came from those two
 * hashes, the column and the row were almost the same number, and the
 * grid collapsed — at 9x5 cells only 11 of 45 were ever used, with up
 * to 10 tickets piled into one. That is why mobs visually merged.
 *
 * Mixing after the hash decorrelates them properly.
 */
function mix32(x: number): number {
  let h = x >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * Deterministic unit value in [0,1) from a key + salt.
 *
 * The salt goes FIRST so the hash streams diverge at byte 0, and the
 * result is avalanche-mixed so different salts are genuinely
 * independent. Both matter — see mix32 above for what happens when
 * they aren't.
 */
export function seededUnit(key: string, salt = ''): number {
  if (!salt) return mix32(stableHash(key)) / 4294967296;
  return mix32(stableHash(salt + '|' + key)) / 4294967296;
}

// Fixed x half-extents. These are constants, not derived from the
// number of tickets, for the same stability reason as placeKeys: a
// count-derived width would stretch the field (and shift every
// piece) the moment the commitment changed. Density changes; the
// frame does not.
export const FIELD_X_HALF = 45;
/** Half the width of the whole plane. Named for the plane rather than
 *  for the band that happened to sit in it. */
export const PLANE_X_HALF = 58;

/**
 * Place `keys` inside a box, deriving each position from the key
 * ALONE.
 *
 * This is the important property: adding or removing a ticket must
 * not move any other ticket. The commitment is editable mid-flight,
 * so an index-derived layout (grid row/col) would reshuffle the
 * whole board every time the operator edits it — exactly the
 * "map that moves on its own" problem we set out to avoid.
 *
 * Implementation: hash each key into a fixed grid cell, then jitter
 * within the cell using a second hash. Even coverage, no index in
 * the maths, fully deterministic. If two keys collide into one cell
 * they land near each other but not exactly on top (different
 * jitter) — acceptable for a sprint-sized commitment.
 */
interface Placed { key: string; x: number; z: number; gx: number; gz: number }

/**
 * Bounded jitter, as a fraction of the cell the piece sits in.
 *
 * Critical for legibility: a piece must stay INSIDE its cell, so two
 * pieces in different cells can only be as close as `cell * (1 -
 * JITTER)`. Keep that above the largest piece's width and cross-cell
 * overlap becomes impossible — only same-cell collisions remain, and
 * those are rare once the grid is the right size.
 */
const JITTER = 0.35;

function placeKeys(
  keys: string[],
  opts: { xHalf: number; zA: number; zB: number; salt: string; cellsX: number; cellsZ: number },
): Placed[] {
  if (keys.length === 0) return [];
  // The grid is FIXED, not derived from the key count: a count-derived
  // grid would restretch the field (and move every piece) whenever the
  // commitment changed. Density changes; the frame does not.
  const { cellsX, cellsZ } = opts;
  const cellW = (opts.xHalf * 2) / cellsX;
  const cellD = (opts.zB - opts.zA) / cellsZ;
  return keys.map((key) => {
    const gx = Math.floor(seededUnit(key, opts.salt + 'gx') * cellsX);
    const gz = Math.floor(seededUnit(key, opts.salt + 'gz') * cellsZ);
    const jx = 0.5 + (seededUnit(key, opts.salt + 'jx') - 0.5) * JITTER;
    const jz = 0.5 + (seededUnit(key, opts.salt + 'jz') - 0.5) * JITTER;
    return {
      key,
      x: -opts.xHalf + (gx + jx) * cellW,
      z: opts.zA + (gz + jz) * cellD,
      gx,
      gz,
    };
  });
}

/**
 * Same-cell collisions are what's left after the grid is sized
 * correctly. Two tickets hashing into one cell would render as one
 * blended mass, so stack them vertically instead — a small lift per
 * occupant, ordered by key so the result is deterministic.
 *
 * This is the one place a piece's position depends on other keys: if
 * a ticket is added to an already-occupied cell, the occupants above
 * the new one shift up. That's vertical-only and rare (about 3 pairs
 * in 50 tickets), and the alternative — every piece floating at a
 * hash-derived height — makes the whole field look bumpy for the sake
 * of 3 collisions.
 */
function staggerStack(cells: Map<string, Placed[]>): Map<string, number> {
  const lift = new Map<string, number>();
  for (const group of cells.values()) {
    if (group.length < 2) continue;
    [...group].sort((a, b) => a.key.localeCompare(b.key)).forEach((p, i) => {
      lift.set(p.key, i * 0.85);
    });
  }
  return lift;
}

// The clock lives in lib/ now — three features need it, and this module is
// about battlefield geometry. Imported for the layout's own use AND
// re-exported, so the callers and tests that already import it from here keep
// working. A bare `export ... from` would not have put it in scope for
// layoutBattlefield, which uses it.
import { elapsedFraction } from '../../lib/campaign-clock';
export { elapsedFraction };

/**
 * Build the whole battlefield. Pure: same input → identical output.
 */
export function layoutBattlefield(input: LayoutInput): Battlefield {
  const committedSet = new Set(input.committed);
  const clearedSet = new Set(input.cleared);

  // Which committed tickets belong to a goal? A goal may also hold
  // keys that aren't committed (the operator can author those); we
  // surface them separately rather than pretending they're progress.
  const goalOf = new Map<string, string>();
  const orphanByGoal = new Map<string, string[]>();
  for (const g of input.goals) {
    const orphans: string[] = [];
    for (const k of g.keys) {
      if (committedSet.has(k)) {
        // First goal wins if a key is somehow listed twice.
        if (!goalOf.has(k)) goalOf.set(k, g.id);
      } else {
        orphans.push(k);
      }
    }
    orphanByGoal.set(g.id, orphans);
  }

  // --- goals as encampments, spaced along x -----------------------
  const goalCount = input.goals.length;
  // Goal spacing is derived from the goal count — goals are authored
  // a handful at a time, not continuously, and they need to be
  // evenly spread. Acceptable: adding a goal may nudge the others.
  const goalSpread = Math.max(18, goalCount * 14);
  const goals: GoalPiece[] = input.goals.map((g, i) => {
    const gx = goalCount === 1 ? 0 : (i / (goalCount - 1)) * 2 - 1;
    const committedKeys = g.keys.filter((k) => committedSet.has(k));
    return {
      id: g.id,
      name: g.name,
      x: gx * (goalSpread / 2),
      z: GOAL_Z,
      total: committedKeys.length,
      cleared: committedKeys.filter((k) => clearedSet.has(k)).length,
      orphanKeys: orphanByGoal.get(g.id) ?? [],
    };
  });

  // --- mobs -------------------------------------------------------
  // Committed tickets that aren't in a goal go to the open field.
  // Committed tickets that ARE in a goal cluster around their camp.
  const openField = input.committed.filter((k) => !goalOf.has(k));
  const xHalf = FIELD_X_HALF;

  const mobs: MobPiece[] = [];
  const minionOf = input.minionOf ?? {};
  const statusOf = input.statusOf ?? {};
  const progressOf = (key: string) => deathProgress(statusOf[key]);
  const isBlocked = (key: string) => statusOf[key] === BLOCKED_STATUS;
  const lastTouchOf = input.lastTouchOf ?? {};
  // 31 x 13 = 403 cells over a 90 x 40 field: cells are ~2.9 units, so
  // with JITTER=0.35 the closest two pieces in adjacent cells can be is
  // 2.9 * 0.65 = 1.89 — comfortably wider than a minion (1.375), so
  // cross-cell overlap cannot happen. Measured on the real 50-ticket
  // commitment this leaves 3 colliding pairs (all same-cell) versus 106
  // with the old 9x5 grid.
  const openFieldPlaced = placeKeys(openField, {
    xHalf, zA: FIELD_Z_FAR, zB: FIELD_Z_NEAR, salt: 'field', cellsX: 31, cellsZ: 13,
  });
  const fieldCells = new Map<string, Placed[]>();
  for (const p of openFieldPlaced) {
    const k = `${p.gx}:${p.gz}`;
    const arr = fieldCells.get(k) ?? [];
    arr.push(p);
    fieldCells.set(k, arr);
  }
  const lift = staggerStack(fieldCells);
  for (const p of openFieldPlaced) {
    mobs.push({
      key: p.key,
      x: p.x,
      z: p.z,
      y: lift.get(p.key) ?? 0,
      groundY: 0,
      cleared: clearedSet.has(p.key),
      goalId: null,
      isReinforcement: false,
      ownerId: input.ownerOf[p.key] ?? null,
      minionBossId: minionOf[p.key] ?? null,
      lastTouchAt: lastTouchOf[p.key] ?? null,
      engaged: isEngaged(statusOf[p.key]),
      appendages: appendagesFor(statusOf[p.key]),
      blocked: isBlocked(p.key),
      progress: progressOf(p.key),
    });
  }

  // --- reinforcements: unplanned work, still in flight --------------
  // Their own band between the field and the graveyard. Placed with
  // placeKeys like everything else, so a ticket's position derives from
  // its key and the band does not reshuffle as arrivals come and go.
  const reinforcements: MobPiece[] = [];
  const reinforceKeys = input.reinforcements ?? [];
  const reinforcePlaced = placeKeys(reinforceKeys, {
    xHalf, zA: REINFORCE_Z_FAR, zB: REINFORCE_Z_NEAR, salt: 'reinforce', cellsX: 31, cellsZ: 5,
  });
  const reinforceCells = new Map<string, Placed[]>();
  for (const p of reinforcePlaced) {
    const k = `${p.gx}:${p.gz}`;
    const arr = reinforceCells.get(k) ?? [];
    arr.push(p);
    reinforceCells.set(k, arr);
  }
  const reinforceLift = staggerStack(reinforceCells);
  for (const p of reinforcePlaced) {
    reinforcements.push({
      key: p.key,
      x: p.x,
      z: p.z,
      y: reinforceLift.get(p.key) ?? 0,
      groundY: 0,
      cleared: clearedSet.has(p.key),
      // Never a goal member: goals track the promise, and this is
      // precisely work that was not promised.
      goalId: null,
      isReinforcement: true,
      ownerId: input.ownerOf[p.key] ?? null,
      minionBossId: minionOf[p.key] ?? null,
      lastTouchAt: lastTouchOf[p.key] ?? null,
      engaged: isEngaged(statusOf[p.key]),
      appendages: appendagesFor(statusOf[p.key]),
      blocked: isBlocked(p.key),
      progress: progressOf(p.key),
    });
  }
  // Goal mobs: a small stable ring around the camp so the camp reads
  // as "a group of work", not a line.
  for (const g of goals) {
    const keys = input.goals.find((x) => x.id === g.id)?.keys.filter((k) => committedSet.has(k)) ?? [];
    for (const k of keys) {
      // Angle and radius come from the key, not the index, so adding
      // a ticket to a goal doesn't rotate everyone else around it.
      const a = seededUnit(k, 'ang') * TWO_PI;
      const r = 5.5 + seededUnit(k, 'rad') * 4.5;
      mobs.push({
        key: k,
        x: g.x + Math.cos(a) * r,
        z: g.z + Math.sin(a) * r * 0.55,
        y: 0,
        groundY: PLATFORM_TOP,
        isReinforcement: false,
        cleared: clearedSet.has(k),
        goalId: g.id,
        ownerId: input.ownerOf[k] ?? null,
        minionBossId: minionOf[k] ?? null,
        lastTouchAt: lastTouchOf[k] ?? null,
        engaged: isEngaged(statusOf[k]),
        appendages: appendagesFor(statusOf[k]),
        blocked: isBlocked(k),
        progress: progressOf(k),
      });
    }
  }

  // --- bosses: the keep, beyond the field -------------------------
  //
  // ONLY THE BOSSES WITH SOMETHING TO FIGHT.
  //
  // Operator: "only show bosses with mobs in the fight, so only load epics to
  // the battlemap where there are related child tickets on the map."
  //
  // A boss whose every child has closed is a vanquished boss, and a keep
  // standing over an empty field says there is still a fight there when there
  // is not. The children counted are the STANDING pieces — the same set the
  // field draws — so a boss is shown exactly while it has mobs on the board.
  const fightingBossIds = new Set(
    [...mobs, ...reinforcements]
      .filter((m) => !m.cleared && m.minionBossId !== null)
      .map((m) => m.minionBossId as string),
  );

  // EVENLY SPACED, AND THAT IS A DELIBERATE REVERSAL.
  //
  // I first placed these from a hash of the boss id, to keep them still when
  // one joined or left the fight. It was the wrong trade and the operator saw
  // it immediately: a hash gives no guarantee of SEPARATION. Measured on the
  // real team, nine bosses landed in six slots — three of them on the same
  // spot — because nine keys over twelve slots collide almost by definition.
  //
  // Three keeps stacked on one place is a broken board. Keeps re-spacing when
  // one is vanquished is merely a different board. So: even spacing over the
  // fighting set, which cannot overlap, and accept that removing a boss
  // re-spaces the rest.
  //
  // The band is wide because the LABELS are: these names run to sixty
  // characters ("EMEA Datum - v7.20 -> v8.0 model migration, the Insatiable")
  // and a tight band puts nine of them through each other.
  const fighting = input.bosses.filter((b) => fightingBossIds.has(b.id));
  const bossSpread = Math.max(60, fighting.length * 26);
  const bosses: BossPiece[] = fighting.map((b, i) => ({
    id: b.id,
    name: b.name,
    x: (fighting.length === 1 ? 0 : (i / (fighting.length - 1)) * 2 - 1) * (bossSpread / 2),
    z: BOSS_Z,
    hpRemaining: Math.max(0, b.hp - b.damage),
    hpMax: b.hp,
  }));

  // --- heroes: stand with the work they are accountable for --------
  // Accountable means ASSIGNED TO, falling back to ownerOf (the most
  // recent actor) when no assignee was ever captured — the same rule
  // ADR-0026 uses for reward and the campaign health view uses for
  // load, so "who owns this" means one thing across the product.
  //
  // Deriving from the assignee rather than the last actor is what puts
  // people on the board who hold work but have not personally moved it
  // last. Measured on the live campaign, that is two of the seventeen
  // players who were previously missing.
  //
  // A hero's position is the centroid of the live mobs they are
  // accountable for.
  const assigneeOf = input.assigneeOf ?? {};
  const actorsOf = input.actorsOf ?? {};
  const standing = [...mobs, ...reinforcements].filter((m) => !m.cleared);

  const owned = new Map<string, MobPiece[]>();
  // Reinforcements count. They are someone's work too, and leaving them
  // out kept 4 players off the board on the live campaign — people who
  // had no committed tickets but were accountable for arrivals.
  //
  // accountable means ASSIGNED TO, and nothing else. It used to fall
  // back to `ownerId` — the last person to move the ticket — which on an
  // UNASSIGNED ticket is simply whoever touched it last. That is not
  // ownership, it is a guess, and it made the board claim somebody owned
  // work nobody had been given. An unassigned ticket is now honestly
  // unowned: no hero stands with it, and nobody is drawn accountable for
  // it. Measured on the live campaign, this leaves 2 of 12 standing
  // tickets unowned and costs one hero.
  for (const m of standing) {
    const accountable = assigneeOf[m.key];
    if (!accountable) continue;
    const arr = owned.get(accountable) ?? [];
    arr.push(m);
    owned.set(accountable, arr);
  }

  // Anyone who ACTED on standing work but is accountable for none of it
  // still needs somewhere to stand.
  //
  // `ownerOf` is the last person to move a ticket, and moves are
  // transitions — so someone whose contribution is comments is never
  // anybody's owner and had no position at all. Their engagement lines
  // could not be drawn, and the board quietly implied they were not
  // involved. Measured on the live campaign: one player was commenting
  // on six open tickets and appeared nowhere.
  //
  // They stand at the centroid of the work they have touched, which is
  // the same rule as an owner — a hero stands with their work, and
  // "their work" is not only what they are accountable for.
  const acted = new Map<string, MobPiece[]>();
  for (const m of standing) {
    for (const actorId of actorsOf[m.key] ?? []) {
      if (owned.has(actorId)) continue; // already has a home
      const arr = acted.get(actorId) ?? [];
      arr.push(m);
      acted.set(actorId, arr);
    }
  }

  const heroes: HeroPiece[] = [...owned.entries(), ...acted.entries()].map(([id, list]) => ({
    id,
    x: list.reduce((s, m) => s + m.x, 0) / list.length,
    z: list.reduce((s, m) => s + m.z, 0) / list.length + 4,
    liveMobs: list.length,
  }));

  // --- the graveyard: side quests, closed and unpromised ----------
  // A fixed grid, not a scatter, and not sized to the number of graves:
  // each key hashes to its own plot, so one more side quest closing
  // never shifts the ones already buried. Plots left empty are simply
  // empty, which is what a graveyard looks like.
  const gravePlaced = placeKeys(input.sideQuests, {
    xHalf: PLANE_X_HALF,
    zA: GRAVE_Z_FAR,
    zB: GRAVE_Z_NEAR,
    salt: 'grave',
    cellsX: GRAVE_COLS,
    cellsZ: GRAVE_ROWS,
  });
  const graveyard: GravePiece[] = gravePlaced.map((p) => ({
    key: p.key,
    x: p.x,
    z: p.z,
  }));

  const frac = elapsedFraction(input.startsAt, input.endsAt);

  return {
    mobs,
    goals,
    bosses,
    heroes,
    reinforcements,
    graveyard,
    standingTotal: mobs.filter((m) => !m.cleared).length
      + reinforcements.filter((m) => !m.cleared).length,
    elapsedFraction: frac,
    remaining: mobs.filter((m) => !m.cleared).length,
    committedTotal: input.committed.length,
    minionsStanding: mobs.filter((m) => !m.cleared && m.minionBossId !== null).length,
  };
}
